import { useCallback } from 'react';
import { generatePath } from 'react-router';
import { ChargeDto } from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import { combineStatus } from 'commons/utils/combineStatus';
import { useGetIsFreePlan, useStoreStatus } from 'domains/Auth/hooks';
import {
  checkoutApi,
  usePatchUpdatePlanChargeMutation,
  usePostAdvancedChargeMutation,
  usePostCreatePayOrderMutation,
} from 'domains/Billing/Checkout/CheckoutApi';
import { checkoutTags } from 'domains/Billing/Checkout/CheckoutApi/config';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import {
  usePaymentMethods,
  GO_TO_CHECKOUT_AFTER_PAYMENT,
  useAfterPaymentRedirection,
  useChargesToPay,
} from 'domains/Billing/Checkout/hooks';
import { VIRTUAL_CHARGE_ID } from 'domains/Billing/Checkout/hooks/usePaymentMethods';

export default function useChargeCreation(initialFetch = true) {
  const { storeStatus } = useStoreStatus();
  const isChurned = storeStatus === 'store_down';
  const { isTrial } = useGetIsFreePlan();
  const { goTo } = useNavegate();

  const dispatch = useAppDispatch();

  const [advancePlanCharge, { status: advancePlanChargeStatus }] =
    usePostAdvancedChargeMutation();
  const [createPayOrder, { status: createPayOrderStatus }] =
    usePostCreatePayOrderMutation();
  const [updatePlanCharge, { status: updatePlanChargeStatus }] =
    usePatchUpdatePlanChargeMutation();

  const { paymentMethods } = usePaymentMethods(initialFetch);

  const { isLoading, isError, status } = combineStatus(
    advancePlanChargeStatus,
    createPayOrderStatus,
    updatePlanChargeStatus,
  );

  const { addAfterPayActionToPath } = useAfterPaymentRedirection();

  const { chargeTotalWithoutTaxes } = useChargesToPay(false);

  const resolveVirtualChargeCreation = useCallback(
    async (virtualCharge?: ChargeDto) => {
      if (virtualCharge?.id !== VIRTUAL_CHARGE_ID) {
        return;
      }
      if (isChurned) {
        const method = paymentMethods?.[0]?.method;
        const option = paymentMethods?.[0]?.recurrencyOptions[0];

        if (!method || !option) return;

        return await updatePlanCharge({
          method,
          recurringFrequency: option.recurringFrequency,
          recurringInterval: option.recurringInterval,
        }).unwrap();
      } else if (isTrial) {
        return await advancePlanCharge().unwrap();
      }
    },
    [isChurned, isTrial, advancePlanCharge, paymentMethods, updatePlanCharge],
  );

  const onContinue = useCallback(
    async (charges: ChargeDto[]) => {
      if (!charges.length) return;
      let createdCharge: ChargeDto | undefined = undefined;
      const virtualPlanCharge = charges?.find(
        ({ id }) => id === VIRTUAL_CHARGE_ID,
      );
      const extraCharges = charges.filter(
        (charge) => charge !== virtualPlanCharge,
      );
      createdCharge = await resolveVirtualChargeCreation(virtualPlanCharge);
      const chargesIds = extraCharges?.map(({ id }) => id) || [];
      const payOrder = await createPayOrder([
        ...(createdCharge ? [createdCharge.id] : []),
        ...chargesIds,
      ])
        .unwrap()
        .finally(() => {
          dispatch(
            checkoutApi.util.invalidateTags([
              checkoutTags.pendingCharges,
              checkoutTags.payOrderToPay,
            ]),
          );
        });
      if (!payOrder) return;
      goTo(
        addAfterPayActionToPath(
          generatePath(CHECKOUT_ROUTES.choosePaymentMethodForPayOrder, {
            payOrderId: payOrder.id,
          }),
          GO_TO_CHECKOUT_AFTER_PAYMENT,
        ),
      );
    },
    [
      createPayOrder,
      goTo,
      dispatch,
      addAfterPayActionToPath,
      resolveVirtualChargeCreation,
    ],
  );

  return {
    onContinue,
    isLoading,
    isError,
    status,
    chargeTotalWithoutTaxes,
    resolveVirtualChargeCreation,
  };
}
